import { ContactUsPayloadModel } from "./interface/contact/contactInterface";
import apiRequest from "../../api/apiRequest";
import { startLoader, stopLoader } from "../../util/loader";
import { enviromentCheck, isTestEnvironment, isValidZipCode, normalizeKeys, removeNullAndEmpty, storeFailedResponse } from "./helperModules/helperFunctions";
import { contactUsPayloadModelKeys  } from "./keys/contactKeys";
import { handleFakeSubmit, normalizeUSZipCode } from "../../util/share";
import { handleMissingData } from "../../util/discovery";
import { successLogger } from "../../api/apiTester";
import { REGEX } from "../../constants/regex";

const plgCodes = {
  code_plg: 'PLG',
  code_plgc: 'PLGC',
  id_plg: 14,
  id_plgc: 256
}

//Check if the payload is valid
function isValidContactPayload(payload: any): payload is ContactUsPayloadModel {
    let payloadLower = JSON.parse(JSON.stringify(normalizeKeys(payload)));
        payloadLower = JSON.parse(JSON.stringify(removeNullAndEmpty(payloadLower)));

    //checking if all mandatory fields are present
    const mandatoryFields = ['isLocalized', 'zipCode', 'email'];
    const hasAllMandatoryFields = mandatoryFields.every(field => payloadLower?.hasOwnProperty(normalizeKeys(field)));
    if (!hasAllMandatoryFields) return false;

    //checking if any invalid keys are present
    const hasInvalidKeys = Object.keys(payloadLower).some((key) => {
      if(!contactUsPayloadModelKeys.hasOwnProperty(key)){
        console.error('Invalid key in Contact Payload:', key);
        return true;
      }
    });
    if (hasInvalidKeys) return false;

    const hasInvalidTypes = Object.entries(payloadLower).some(([key, value]) => {
        if (value === undefined) {
          console.error('Undefined value in Contact Payload:', key);
          return false;
        }
        return typeof value !== contactUsPayloadModelKeys[key];
    });

    if (hasInvalidTypes) return false;

    //check for valid Zipcode and updated trimmed zipcode
    if (!isValidZipCode(payloadLower.zipCode)) return false;

    payload.ZipCode = isValidZipCode(payloadLower.zipCode);

    //check for the enviroment and send in the payload
    payload.IsTest = isTestEnvironment();
    return payload;
}

//prepoupulate the payload with the required fields
async function preparePayload(payload: any): Promise<ContactUsPayloadModel> {
  payload.isLocalized = !!payload.franchiseWebLocationId;

  if (!payload.conceptId && !payload.ConceptId) {
    console.error('Concept Id is missing in the payload');
  }

  payload.address = payload.address ?? "No address collected";
  payload.address = payload.address.replace(/^,/, '').trim();

  payload.comments = payload.comments?.replace(/^[ \t]*,+/, '').trim();
    if(!REGEX.sendCaPostalCode.test(payload.zipCode) && payload?.zipCode?.includes('-')) {
      payload.ZipCode = normalizeUSZipCode(payload.zipCode);
      payload.zipCode = normalizeUSZipCode(payload.zipCode);
  }
  const city = payload.city ?? payload.City;
  const state = payload.state ?? payload.State;
  const zipCode = payload.zipCode ?? payload.ZipCode;

  try {
    const { city: updatedCity, state: updatedState } = await handleMissingData(city, state, zipCode, true);
    payload.city = updatedCity ?? city;
    payload.state = updatedState ?? state;
  } catch (error) {
    console.error('Error in preparing payload for Contact us endpoint', error);
  }

  return payload;
}

/**
 * Normalizes concept ID for PLG/PLGC brands to ensure API compatibility.
 * 
 * This function performs the following transformations:
 * 1. Defaults missing conceptId to PLG (14)
 * 2. Converts string conceptId to number if valid
 * 3. Replaces PLGC (256) with PLG (14) for backend compatibility
 * 
 * @param {ContactUsPayloadModel} payload - The contact us payload to process
 * @returns {ContactUsPayloadModel} The payload with normalized conceptId
 * @throws {Error} If payload is null, undefined, or not a valid object
 * 
 * @example
 * // Missing conceptId - defaults to PLG
 * findAndReplacePlgcConceptId({ email: 'test@example.com' });
 * // Returns: { email: 'test@example.com', conceptId: 14 }
 * 
 * @example
 * // String conceptId - converts to number
 * findAndReplacePlgcConceptId({ conceptId: "256" });
 * // Returns: { conceptId: 14 } (PLGC converted to PLG)
 * 
 * @example
 * // PLGC to PLG conversion
 * findAndReplacePlgcConceptId({ conceptId: 256 });
 * // Returns: { conceptId: 14 }
 * 
 * @security Input validation prevents null/undefined payloads
 * @complexity O(1) - Constant time operations
 */
function findAndReplacePlgcConceptId(payload: ContactUsPayloadModel): ContactUsPayloadModel {
  // Security: Validate input

  const conceptId = (document.getElementById("conceptId") as HTMLInputElement)?.value ?? payload?.conceptId;
  if(Number(conceptId) !== plgCodes.id_plgc && Number(conceptId) !== plgCodes.id_plg){
    return payload;
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Invalid payload: payload must be a valid object');
  }
  if(!payload.conceptId)
    payload.conceptId = plgCodes.id_plg;
  
  if(!payload.conceptCode)
    payload.conceptCode = plgCodes.code_plg;
  // Convert string to number if it's a valid numeric string
  if (typeof payload.conceptId === 'string') {
    const numericId = Number(payload.conceptId);
    if (!Number.isNaN(numericId)) {
      payload.conceptId = numericId;
    } 
  }

  if((payload.conceptCode).toUpperCase() === plgCodes.code_plgc)
    payload.conceptCode = plgCodes.code_plg;

  if(payload.conceptId === plgCodes.id_plgc)
    payload.conceptId = plgCodes.id_plg;
  return payload;

}

//Calling the ContactUS end point
export async function contactUsPostCallWrapper(url: string, payload: any) {
  const suLogger = localStorage.getItem("suLogger") === 'true';
  //returns true if pass and false if fail
  if(!enviromentCheck(url)){
    stopLoader();
    return false;
  }

  const enableStrictValidation = process.env.ENABLE_STRICT_PAYLOAD_VALIDATION?.toLowerCase() === 'true';
  payload = await preparePayload(payload);
  if(enableStrictValidation){
    if (!isValidContactPayload(payload)) {
      console.error('Invalid payload for Contact us Endpoint:', payload);
      throw new Error(`Invalid payload for Contact us Endpoint: ${JSON.stringify(payload)}`);
    }
  }
  else{
    console.log("Strict Payload validation disabled") 
  }
  const request = {
    method: 'POST',
    url,
    data: JSON.parse(JSON.stringify(normalizeKeys(findAndReplacePlgcConceptId(payload)))),
  };

  //Code to handle Fake Submit
  if (request?.data?.firstName?.toLowerCase().includes("isfakesubmit") || request?.data?.lastName?.toLowerCase().includes("isfakesubmit")) {
    handleFakeSubmit(request).then(() => {
      console.log("Mock Request", request);
    }).catch((error) => {
      console.error("Error in Fake Submit:", error);
    }).finally(() => {
      return Promise.resolve(true);
    });
    const formdata = JSON.stringify(payload);
    sessionStorage.setItem('fakeSubmitData',formdata);
    return true;
  }

  try {
    startLoader();
    const response = await apiRequest(request);
    successLogger(request.data, request.url, suLogger, response);
    if(response)
      sessionStorage.setItem('contactResponse', JSON.stringify(response));
    return response;
  } catch (err) {
    console.error('Error:', err);
    storeFailedResponse(request, err);
    throw(err);
  } finally {
    stopLoader();
  }
}


export const contactUsPostCallWrapperHelper = { isValidContactPayload, preparePayload, findAndReplacePlgcConceptId };